import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  Calendar,
  Clock,
  Users,
  MapPin,
  Plus,
  Trash2,
  Edit,
  Check,
  X,
  AlertCircle,
  CheckCircle,
  ArrowLeft,
  Download,
  Mail,
  Phone,
  User,
  MessageSquare,
  RefreshCw,
  Copy,
  ExternalLink,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';
import './SchedulingPage.css';

/**
 * SchedulingPage - Standalone admin page for managing artwork drop-off/pick-up scheduling
 * URL: /curator/collections/:collectionId/scheduling
 * 
 * Replaces SignUpGenius with built-in scheduling functionality:
 * - Create schedule windows (drop-off, pick-up) with date/time/location
 * - Auto-generate timeslots (e.g., 30-minute intervals)
 * - View artist reservations
 * - Track arrivals
 * - Export schedule data
 */
const SchedulingPage = () => {
  const { collectionId } = useParams();
  const navigate = useNavigate();

  // Collection state
  const [collection, setCollection] = useState(null);
  const [eventDetails, setEventDetails] = useState(null);
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Scheduling state
  const [scheduleWindows, setScheduleWindows] = useState([]);
  const [timeslots, setTimeslots] = useState([]);
  const [reservations, setReservations] = useState([]);

  // UI state
  const [activeSection, setActiveSection] = useState('overview');
  const [showCreateWindow, setShowCreateWindow] = useState(false);
  const [editingWindow, setEditingWindow] = useState(null);
  const [notification, setNotification] = useState(null);
  const [expandedWindows, setExpandedWindows] = useState(new Set());

  // Form state for creating/editing windows
  const [windowForm, setWindowForm] = useState({
    type: 'drop_off',
    window_date: '',
    start_time: '15:00',
    end_time: '19:00',
    location: '',
    notes: '',
    slot_duration: 30,
    max_capacity: 10
  });

  // Load collection and scheduling data
  useEffect(() => {
    loadData();
  }, [collectionId]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Load collection
      const { data: collectionData, error: collectionError } = await supabase
        .from('collections')
        .select('*')
        .eq('collection_id', collectionId)
        .single();

      if (collectionError) throw collectionError;
      setCollection(collectionData);

      // Load event if exists
      const { data: eventData } = await supabase
        .from('events')
        .select('*, galleries(*)')
        .eq('collection_id', collectionId)
        .single();
      
      if (eventData) {
        setEventDetails(eventData);
        // Pre-fill location from gallery
        if (eventData.galleries?.name) {
          setWindowForm(prev => ({
            ...prev,
            location: eventData.galleries.name + (eventData.galleries.address ? `, ${eventData.galleries.address}` : '')
          }));
        }
      }

      // Load artists in this collection
      const { data: artworksData } = await supabase
        .from('collection_vc_mapping')
        .select(`
          vc_id,
          vcs!inner(
            vc_id,
            uuid,
            users!inner(uuid, first_name, last_name, nickname, email, mobile_phone, handle)
          )
        `)
        .eq('collection_id', collectionId)
        .eq('accepted', true);

      if (artworksData) {
        const uniqueArtists = new Map();
        artworksData.forEach(item => {
          const user = item.vcs?.users;
          if (user && !uniqueArtists.has(user.uuid)) {
            uniqueArtists.set(user.uuid, user);
          }
        });
        setArtists(Array.from(uniqueArtists.values()));
      }

      // Load schedule windows
      await loadScheduleData();

    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadScheduleData = async () => {
    try {
      // Load windows for this collection
      const { data: windowsData, error: windowsError } = await supabase
        .from('collection_schedule_windows')
        .select('*')
        .eq('collection_id', collectionId)
        .order('window_date', { ascending: true })
        .order('start_time', { ascending: true });

      if (windowsError) throw windowsError;
      setScheduleWindows(windowsData || []);

      if (windowsData && windowsData.length > 0) {
        const windowIds = windowsData.map(w => w.id);

        // Load timeslots for these windows
        const { data: slotsData, error: slotsError } = await supabase
          .from('schedule_timeslots')
          .select('*')
          .in('window_id', windowIds)
          .order('slot_start', { ascending: true });

        if (slotsError) throw slotsError;
        setTimeslots(slotsData || []);

        if (slotsData && slotsData.length > 0) {
          const slotIds = slotsData.map(s => s.id);

          // Load reservations with user info
          const { data: reservationsData, error: reservationsError } = await supabase
            .from('schedule_reservations')
            .select(`
              *,
              users:user_id(uuid, first_name, last_name, nickname, email, mobile_phone, handle)
            `)
            .in('timeslot_id', slotIds)
            .order('created_at', { ascending: true });

          if (reservationsError) throw reservationsError;
          setReservations(reservationsData || []);
        }
      }
    } catch (err) {
      console.error('Error loading schedule data:', err);
    }
  };

  // Create a new schedule window with auto-generated timeslots
  const createScheduleWindow = async () => {
    try {
      // Create the window
      const { data: windowData, error: windowError } = await supabase
        .from('collection_schedule_windows')
        .insert({
          collection_id: parseInt(collectionId),
          event_id: eventDetails?.id || null,
          type: windowForm.type,
          window_date: windowForm.window_date,
          start_time: windowForm.start_time,
          end_time: windowForm.end_time,
          location: windowForm.location,
          notes: windowForm.notes
        })
        .select()
        .single();

      if (windowError) throw windowError;

      // Auto-generate timeslots
      const slots = generateTimeslots(
        windowForm.start_time,
        windowForm.end_time,
        windowForm.slot_duration,
        windowForm.max_capacity
      );

      if (slots.length > 0) {
        const slotsToInsert = slots.map(slot => ({
          window_id: windowData.id,
          slot_start: slot.start,
          slot_end: slot.end,
          max_capacity: windowForm.max_capacity
        }));

        const { error: slotsError } = await supabase
          .from('schedule_timeslots')
          .insert(slotsToInsert);

        if (slotsError) throw slotsError;
      }

      setShowCreateWindow(false);
      setWindowForm({
        type: 'drop_off',
        window_date: '',
        start_time: '15:00',
        end_time: '19:00',
        location: eventDetails?.galleries?.name || '',
        notes: '',
        slot_duration: 30,
        max_capacity: 10
      });
      
      showNotification('success', `${windowForm.type === 'drop_off' ? 'Drop-off' : 'Pick-up'} window created with ${slots.length} timeslots`);
      await loadScheduleData();
    } catch (err) {
      console.error('Error creating window:', err);
      showNotification('error', 'Failed to create schedule window');
    }
  };

  // Generate timeslot objects from start/end time
  const generateTimeslots = (startTime, endTime, durationMinutes, maxCapacity) => {
    const slots = [];
    let current = parseTime(startTime);
    const end = parseTime(endTime);

    while (current < end) {
      const slotEnd = current + durationMinutes;
      if (slotEnd <= end + durationMinutes) { // Allow last slot to potentially exceed slightly
        slots.push({
          start: formatTime(current),
          end: formatTime(Math.min(slotEnd, end))
        });
      }
      current = slotEnd;
    }
    return slots;
  };

  const parseTime = (timeStr) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const formatTime = (minutes) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  };

  const formatTimeDisplay = (timeStr) => {
    const [hours, minutes] = timeStr.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T12:00:00');
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Delete a schedule window and its timeslots/reservations
  const deleteWindow = async (windowId) => {
    if (!confirm('Delete this schedule window and all its reservations?')) return;

    try {
      const { error } = await supabase
        .from('collection_schedule_windows')
        .delete()
        .eq('id', windowId);

      if (error) throw error;

      showNotification('success', 'Schedule window deleted');
      await loadScheduleData();
    } catch (err) {
      console.error('Error deleting window:', err);
      showNotification('error', 'Failed to delete window');
    }
  };

  // Mark artist as arrived
  const toggleArrived = async (reservationId, currentStatus) => {
    try {
      const { error } = await supabase
        .from('schedule_reservations')
        .update({
          arrived: !currentStatus,
          arrived_at: !currentStatus ? new Date().toISOString() : null
        })
        .eq('id', reservationId);

      if (error) throw error;
      await loadScheduleData();
    } catch (err) {
      console.error('Error updating arrival:', err);
      showNotification('error', 'Failed to update arrival status');
    }
  };

  // Delete a reservation
  const deleteReservation = async (reservationId) => {
    if (!confirm('Remove this reservation?')) return;

    try {
      const { error } = await supabase
        .from('schedule_reservations')
        .delete()
        .eq('id', reservationId);

      if (error) throw error;
      showNotification('success', 'Reservation removed');
      await loadScheduleData();
    } catch (err) {
      console.error('Error deleting reservation:', err);
      showNotification('error', 'Failed to remove reservation');
    }
  };

  // Get reservations for a specific timeslot
  const getSlotReservations = (slotId) => {
    return reservations.filter(r => r.timeslot_id === slotId);
  };

  // Get timeslots for a specific window
  const getWindowTimeslots = (windowId) => {
    return timeslots.filter(t => t.window_id === windowId);
  };

  // Calculate stats for a window
  const getWindowStats = (windowId) => {
    const windowSlots = getWindowTimeslots(windowId);
    const windowReservations = reservations.filter(r => 
      windowSlots.some(s => s.id === r.timeslot_id)
    );
    const totalCapacity = windowSlots.reduce((sum, s) => sum + (s.max_capacity || 10), 0);
    const arrivedCount = windowReservations.filter(r => r.arrived).length;

    return {
      totalSlots: windowSlots.length,
      totalCapacity,
      reservations: windowReservations.length,
      arrived: arrivedCount,
      available: totalCapacity - windowReservations.length
    };
  };

  // Toggle window expanded state
  const toggleWindowExpanded = (windowId) => {
    setExpandedWindows(prev => {
      const next = new Set(prev);
      if (next.has(windowId)) {
        next.delete(windowId);
      } else {
        next.add(windowId);
      }
      return next;
    });
  };

  // Export schedule to CSV
  const exportScheduleCSV = () => {
    const rows = [['Window Type', 'Date', 'Time Slot', 'Artist Name', 'Email', 'Phone', 'Comment', 'Arrived', 'Location']];

    scheduleWindows.forEach(window => {
      const windowSlots = getWindowTimeslots(window.id);
      windowSlots.forEach(slot => {
        const slotReservations = getSlotReservations(slot.id);
        if (slotReservations.length === 0) {
          rows.push([
            window.type === 'drop_off' ? 'Drop-off' : 'Pick-up',
            formatDate(window.window_date),
            `${formatTimeDisplay(slot.slot_start)} - ${formatTimeDisplay(slot.slot_end)}`,
            '(Available)',
            '', '', '', '', window.location
          ]);
        } else {
          slotReservations.forEach(res => {
            const name = res.users?.nickname || 
              `${res.users?.first_name || res.artist_name || ''} ${res.users?.last_name || ''}`.trim() || 
              'Unknown';
            rows.push([
              window.type === 'drop_off' ? 'Drop-off' : 'Pick-up',
              formatDate(window.window_date),
              `${formatTimeDisplay(slot.slot_start)} - ${formatTimeDisplay(slot.slot_end)}`,
              name,
              res.users?.email || res.artist_email || '',
              res.users?.mobile_phone || res.artist_phone || '',
              res.comment || '',
              res.arrived ? 'Yes' : 'No',
              window.location
            ]);
          });
        }
      });
    });

    const csv = rows.map(row => row.map(cell => `"${(cell || '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${collection?.title || 'schedule'}-schedule.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Copy artist signup link
  const copySignupLink = () => {
    const link = `${window.location.origin}/schedule/${collectionId}`;
    navigator.clipboard.writeText(link);
    showNotification('success', 'Signup link copied to clipboard');
  };

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 3000);
  };

  if (loading) {
    return (
      <div className="scheduling-page">
        <div className="scheduling-loading">
          <RefreshCw className="spinning" size={32} />
          <p>Loading scheduling data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="scheduling-page">
        <div className="scheduling-error">
          <AlertCircle size={48} />
          <h3>Error Loading Data</h3>
          <p>{error}</p>
          <button onClick={loadData} className="retry-button">
            <RefreshCw size={16} />
            Retry
          </button>
        </div>
      </div>
    );
  }

  const totalStats = {
    windows: scheduleWindows.length,
    reservations: reservations.length,
    arrived: reservations.filter(r => r.arrived).length,
    dropOffWindows: scheduleWindows.filter(w => w.type === 'drop_off').length,
    pickUpWindows: scheduleWindows.filter(w => w.type === 'pick_up').length
  };

  return (
    <div className="scheduling-page">
      {/* Header */}
      <div className="scheduling-header">
        <div className="scheduling-header-left">
          <button 
            onClick={() => navigate(`/curator/collections/${collectionId}`)}
            className="back-button"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="scheduling-title-section">
            <h1>Artwork Scheduling</h1>
            <p className="collection-name">{collection?.title}</p>
          </div>
        </div>
        <div className="scheduling-header-actions">
          <button onClick={copySignupLink} className="action-btn secondary">
            <Copy size={16} />
            Copy Signup Link
          </button>
          <button onClick={exportScheduleCSV} className="action-btn secondary">
            <Download size={16} />
            Export CSV
          </button>
          <button 
            onClick={() => setShowCreateWindow(true)} 
            className="action-btn primary"
          >
            <Plus size={16} />
            Create Window
          </button>
        </div>
      </div>

      {/* Notification */}
      {notification && (
        <div className={`scheduling-notification ${notification.type}`}>
          {notification.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
          {notification.message}
        </div>
      )}

      {/* Stats Overview */}
      <div className="scheduling-stats">
        <div className="stat-card">
          <Calendar size={24} />
          <div className="stat-content">
            <span className="stat-value">{totalStats.windows}</span>
            <span className="stat-label">Schedule Windows</span>
          </div>
        </div>
        <div className="stat-card">
          <Users size={24} />
          <div className="stat-content">
            <span className="stat-value">{totalStats.reservations}</span>
            <span className="stat-label">Reservations</span>
          </div>
        </div>
        <div className="stat-card arrived">
          <CheckCircle size={24} />
          <div className="stat-content">
            <span className="stat-value">{totalStats.arrived}</span>
            <span className="stat-label">Arrived</span>
          </div>
        </div>
        <div className="stat-card">
          <User size={24} />
          <div className="stat-content">
            <span className="stat-value">{artists.length}</span>
            <span className="stat-label">Accepted Artists</span>
          </div>
        </div>
      </div>

      {/* Create Window Modal */}
      {showCreateWindow && (
        <div className="modal-overlay" onClick={() => setShowCreateWindow(false)}>
          <div className="modal-content create-window-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create Schedule Window</h3>
              <button onClick={() => setShowCreateWindow(false)} className="modal-close">
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <label>Type</label>
                <div className="type-selector">
                  <button
                    type="button"
                    className={`type-btn ${windowForm.type === 'drop_off' ? 'active' : ''}`}
                    onClick={() => setWindowForm(prev => ({ ...prev, type: 'drop_off' }))}
                  >
                    📦 Drop-off
                  </button>
                  <button
                    type="button"
                    className={`type-btn ${windowForm.type === 'pick_up' ? 'active' : ''}`}
                    onClick={() => setWindowForm(prev => ({ ...prev, type: 'pick_up' }))}
                  >
                    🚚 Pick-up
                  </button>
                </div>
              </div>

              <div className="form-row">
                <label>Date</label>
                <input
                  type="date"
                  value={windowForm.window_date}
                  onChange={e => setWindowForm(prev => ({ ...prev, window_date: e.target.value }))}
                  required
                />
              </div>

              <div className="form-row-group">
                <div className="form-row">
                  <label>Start Time</label>
                  <input
                    type="time"
                    value={windowForm.start_time}
                    onChange={e => setWindowForm(prev => ({ ...prev, start_time: e.target.value }))}
                  />
                </div>
                <div className="form-row">
                  <label>End Time</label>
                  <input
                    type="time"
                    value={windowForm.end_time}
                    onChange={e => setWindowForm(prev => ({ ...prev, end_time: e.target.value }))}
                  />
                </div>
              </div>

              <div className="form-row-group">
                <div className="form-row">
                  <label>Slot Duration (minutes)</label>
                  <select
                    value={windowForm.slot_duration}
                    onChange={e => setWindowForm(prev => ({ ...prev, slot_duration: parseInt(e.target.value) }))}
                  >
                    <option value={15}>15 minutes</option>
                    <option value={30}>30 minutes</option>
                    <option value={45}>45 minutes</option>
                    <option value={60}>60 minutes</option>
                  </select>
                </div>
                <div className="form-row">
                  <label>Max per Slot</label>
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={windowForm.max_capacity}
                    onChange={e => setWindowForm(prev => ({ ...prev, max_capacity: parseInt(e.target.value) || 10 }))}
                  />
                </div>
              </div>

              <div className="form-row">
                <label>Location</label>
                <input
                  type="text"
                  value={windowForm.location}
                  onChange={e => setWindowForm(prev => ({ ...prev, location: e.target.value }))}
                  placeholder="e.g., Bridgeport Art Center"
                />
              </div>

              <div className="form-row">
                <label>Notes (optional)</label>
                <textarea
                  value={windowForm.notes}
                  onChange={e => setWindowForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Instructions for artists..."
                  rows={3}
                />
              </div>

              <div className="form-preview">
                <p>
                  This will create {generateTimeslots(windowForm.start_time, windowForm.end_time, windowForm.slot_duration, windowForm.max_capacity).length} timeslots, 
                  each with capacity for {windowForm.max_capacity} artists.
                </p>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowCreateWindow(false)} className="btn-cancel">
                Cancel
              </button>
              <button 
                onClick={createScheduleWindow} 
                className="btn-create"
                disabled={!windowForm.window_date}
              >
                <Plus size={16} />
                Create Window
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Windows */}
      <div className="schedule-windows">
        {scheduleWindows.length === 0 ? (
          <div className="empty-state">
            <Calendar size={64} />
            <h3>No Schedule Windows Yet</h3>
            <p>Create a drop-off or pick-up window for artists to reserve their timeslot.</p>
            <button onClick={() => setShowCreateWindow(true)} className="action-btn primary">
              <Plus size={16} />
              Create First Window
            </button>
          </div>
        ) : (
          scheduleWindows.map(window => {
            const stats = getWindowStats(window.id);
            const windowSlots = getWindowTimeslots(window.id);
            const isExpanded = expandedWindows.has(window.id);

            return (
              <div key={window.id} className={`schedule-window ${window.type}`}>
                <div 
                  className="window-header"
                  onClick={() => toggleWindowExpanded(window.id)}
                >
                  <div className="window-info">
                    <span className={`window-type-badge ${window.type}`}>
                      {window.type === 'drop_off' ? '📦 Drop-off' : '🚚 Pick-up'}
                    </span>
                    <h3>{formatDate(window.window_date)}</h3>
                    <p className="window-time">
                      <Clock size={14} />
                      {formatTimeDisplay(window.start_time)} – {formatTimeDisplay(window.end_time)}
                    </p>
                    {window.location && (
                      <p className="window-location">
                        <MapPin size={14} />
                        {window.location}
                      </p>
                    )}
                  </div>
                  <div className="window-stats">
                    <span className="stat">
                      <Users size={14} />
                      {stats.reservations}/{stats.totalCapacity} reserved
                    </span>
                    <span className="stat arrived">
                      <CheckCircle size={14} />
                      {stats.arrived} arrived
                    </span>
                    <button className="expand-btn">
                      {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </button>
                  </div>
                  <div className="window-actions" onClick={e => e.stopPropagation()}>
                    <button 
                      onClick={() => deleteWindow(window.id)}
                      className="delete-btn"
                      title="Delete window"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="window-timeslots">
                    {windowSlots.map(slot => {
                      const slotReservations = getSlotReservations(slot.id);
                      const available = (slot.max_capacity || 10) - slotReservations.length;

                      return (
                        <div key={slot.id} className="timeslot">
                          <div className="timeslot-header">
                            <span className="timeslot-time">
                              {formatTimeDisplay(slot.slot_start)} – {formatTimeDisplay(slot.slot_end)}
                            </span>
                            <span className={`timeslot-availability ${available <= 0 ? 'full' : available <= 2 ? 'limited' : 'available'}`}>
                              {available <= 0 ? 'Full' : `${available} available`}
                            </span>
                          </div>
                          
                          {slotReservations.length > 0 && (
                            <div className="slot-reservations">
                              {slotReservations.map(res => {
                                const artistName = res.users?.nickname || 
                                  `${res.users?.first_name || res.artist_name || ''} ${res.users?.last_name || ''}`.trim() || 
                                  'Unknown Artist';
                                const email = res.users?.email || res.artist_email;
                                const phone = res.users?.mobile_phone || res.artist_phone;

                                return (
                                  <div key={res.id} className={`reservation ${res.arrived ? 'arrived' : ''}`}>
                                    <div className="reservation-main">
                                      <button
                                        onClick={() => toggleArrived(res.id, res.arrived)}
                                        className={`arrival-toggle ${res.arrived ? 'arrived' : ''}`}
                                        title={res.arrived ? 'Mark as not arrived' : 'Mark as arrived'}
                                      >
                                        {res.arrived ? <CheckCircle size={18} /> : <div className="arrival-circle" />}
                                      </button>
                                      <div className="reservation-info">
                                        <span className="artist-name">{artistName}</span>
                                        <div className="artist-contact">
                                          {email && (
                                            <a href={`mailto:${email}`} className="contact-link">
                                              <Mail size={12} />
                                              {email}
                                            </a>
                                          )}
                                          {phone && (
                                            <a href={`tel:${phone}`} className="contact-link">
                                              <Phone size={12} />
                                              {phone}
                                            </a>
                                          )}
                                        </div>
                                        {res.comment && (
                                          <p className="reservation-comment">
                                            <MessageSquare size={12} />
                                            {res.comment}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                    <button
                                      onClick={() => deleteReservation(res.id)}
                                      className="delete-reservation"
                                      title="Remove reservation"
                                    >
                                      <X size={14} />
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default SchedulingPage;
